import { getDadDisplayName, type Mission, type ContributionRole } from '../types';
import { getCurrentProfile } from '../services/authService';
import { enlistInMission } from '../services/missionService';
import { showToast } from '../components/Toast';

export function renderEnlistModal(mission: Mission): string {
  return `
    <div id="modal-enlist" class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto modal-backdrop animate-fade-in">
      <div class="bg-[#0a0c13] border border-cyan-400/40 shadow-[0_0_35px_rgba(0,240,255,0.15)] rounded-2xl max-w-lg w-full p-5 sm:p-6 space-y-4 my-8 relative overflow-hidden">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
        
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-white/[0.08] pb-3">
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-xl bg-transparent text-cyan-400 flex items-center justify-center font-bold border border-cyan-400/50 shadow-[0_0_12px_rgba(0,240,255,0.2)]">
              <i class="fa-solid fa-hand-fist"></i>
            </div>
            <div>
              <h3 class="text-base font-black text-slate-100 tracking-tight">Enlist in Mission</h3>
              <p class="text-[11px] text-slate-400">Join your neighborhood crew shoulder-to-shoulder</p>
            </div>
          </div>
          <button id="close-enlist-modal-btn" class="text-slate-400 hover:text-slate-200 p-1">
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <!-- Preview Box -->
        <div class="p-3 bg-[#06070a] rounded-xl text-xs space-y-1 border border-white/[0.08]">
          <div class="font-bold text-slate-200">${mission.title}</div>
          <div class="text-slate-400 text-[11px]">${mission.datetime} &bull; ${mission.locationMasked}</div>
        </div>

        <!-- Contribution Role Selection -->
        <div class="space-y-2">
          <label class="block text-xs font-bold text-slate-300 uppercase tracking-wider">
            Your Self-Declared Contribution Role:
          </label>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            
            <label class="flex items-start gap-2 p-2.5 rounded-xl border border-white/[0.1] bg-[#06070a]/60 cursor-pointer hover:border-cyan-400/50 has-[:checked]:border-cyan-400 has-[:checked]:shadow-[0_0_15px_rgba(0,240,255,0.12)] transition-all">
              <input type="radio" name="enlist-role" value="experienced" class="mt-0.5 text-cyan-400 focus:ring-0 accent-cyan-400">
              <div>
                <span class="font-bold text-slate-200 block">Experienced Peer</span>
                <span class="text-[10px] text-slate-400">Done this before; happy to share DIY hobby tips (non-contractor).</span>
              </div>
            </label>

            <label class="flex items-start gap-2 p-2.5 rounded-xl border border-white/[0.1] bg-[#06070a]/60 cursor-pointer hover:border-cyan-400/50 has-[:checked]:border-cyan-400 has-[:checked]:shadow-[0_0_15px_rgba(0,240,255,0.12)] transition-all">
              <input type="radio" name="enlist-role" value="helper" checked class="mt-0.5 text-cyan-400 focus:ring-0 accent-cyan-400">
              <div>
                <span class="font-bold text-slate-200 block">Capable Pair of Hands</span>
                <span class="text-[10px] text-slate-400">Ready for heavy lifting, holding boards, or whatever is needed.</span>
              </div>
            </label>

            <label class="flex items-start gap-2 p-2.5 rounded-xl border border-white/[0.1] bg-[#06070a]/60 cursor-pointer hover:border-cyan-400/50 has-[:checked]:border-cyan-400 has-[:checked]:shadow-[0_0_15px_rgba(0,240,255,0.12)] transition-all">
              <input type="radio" name="enlist-role" value="learner" class="mt-0.5 text-cyan-400 focus:ring-0 accent-cyan-400">
              <div>
                <span class="font-bold text-slate-200 block">Apprentice / Learner</span>
                <span class="text-[10px] text-slate-400">Eager to learn hands-on while pitching in.</span>
              </div>
            </label>

            <label class="flex items-start gap-2 p-2.5 rounded-xl border border-white/[0.1] bg-[#06070a]/60 cursor-pointer hover:border-cyan-400/50 has-[:checked]:border-cyan-400 has-[:checked]:shadow-[0_0_15px_rgba(0,240,255,0.12)] transition-all">
              <input type="radio" name="enlist-role" value="supplies" class="mt-0.5 text-cyan-400 focus:ring-0 accent-cyan-400">
              <div>
                <span class="font-bold text-slate-200 block">Quartermaster</span>
                <span class="text-[10px] text-slate-400">Bringing cooler, ice water, snacks, or supplemental PPE gear.</span>
              </div>
            </label>

          </div>
        </div>

        <!-- Optional Tool Pledge -->
        <div class="space-y-1">
          <label class="block text-xs font-bold text-slate-300 uppercase tracking-wider">
            Can you bring any tools? (Optional)
          </label>
          <input type="text" id="enlist-tools-pledge-input" placeholder="e.g., Cordless drill, extra work gloves, 4ft level" class="w-full bg-[#06070a] border border-white/[0.12] text-slate-200 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)]">
        </div>

        <!-- MANDATORY CLICKWRAP LIABILITY WAIVER & ASSUMPTION OF RISK -->
        <div class="p-3.5 bg-transparent border border-pink-500/40 rounded-xl space-y-2 relative overflow-hidden">
          <div class="laser-line-pink absolute top-0 left-0 right-0"></div>
          <div class="flex items-center gap-1.5 text-pink-400 text-xs font-bold">
            <i class="fa-solid fa-triangle-exclamation"></i> Mandatory Assumption of Risk Waiver
          </div>
          <label class="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" id="enlist-waiver-checkbox" class="mt-1 h-4 w-4 rounded border-white/20 text-pink-500 focus:ring-0 accent-pink-500 bg-[#06070a]">
            <span class="text-[11px] text-slate-300 leading-snug">
              I acknowledge that <strong>Pops Culture is strictly a communications tool</strong> and does not vet participants, projects, tools, or building sites. I am voluntarily choosing to participate at my own risk and agree to hold harmless Pops Culture from any injury, damage, or dispute. I am responsible for assessing my own physical limits and using proper personal protective equipment (PPE).
            </span>
          </label>
        </div>

        <!-- Submit Buttons -->
        <div class="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.08]">
          <button id="cancel-enlist-btn" class="px-4 py-2 rounded-lg text-xs font-semibold text-slate-400 hover:text-slate-200 hover:border hover:border-white/[0.15]">
            Cancel
          </button>
          <button id="confirm-enlist-btn" class="btn-primary px-5 py-2.5 rounded-xl text-xs flex items-center gap-2 font-black">
            <i class="fa-solid fa-check"></i> Complete Enlistment
          </button>
        </div>

      </div>
    </div>
  `;
}

export function bindEnlistModalEvents(
  mission: Mission,
  onSuccess: () => void,
  onCancel: () => void
): void {
  document.getElementById('close-enlist-modal-btn')?.addEventListener('click', () => {
    onCancel();
  });
  document.getElementById('cancel-enlist-btn')?.addEventListener('click', () => {
    onCancel();
  });

  document.getElementById('confirm-enlist-btn')?.addEventListener('click', async () => {
    const waiverCheck = document.getElementById('enlist-waiver-checkbox') as HTMLInputElement;
    if (!waiverCheck?.checked) {
      showToast('You must review and check the liability waiver to enlist.', 'warning');
      return;
    }

    const selectedRadio = document.querySelector('input[name="enlist-role"]:checked') as HTMLInputElement;
    const roleType = (selectedRadio?.value || 'helper') as ContributionRole;

    const roleMap: Record<ContributionRole, string> = {
      experienced: 'Experienced Peer',
      helper: 'Capable Pair of Hands',
      learner: 'Apprentice / Learner',
      supplies: 'Quartermaster',
      host: 'Event Host'
    };

    const toolInput = document.getElementById('enlist-tools-pledge-input') as HTMLInputElement;
    const toolPledged = toolInput?.value.trim() || '';

    const profile = getCurrentProfile();
    if (!profile) {
      showToast('Please sign in to enlist.', 'warning');
      return;
    }

    try {
      await enlistInMission(
        mission.id,
        { uid: profile.uid, name: getDadDisplayName(profile), avatarInitials: profile.avatarInitials },
        roleMap[roleType] || 'Capable Pair of Hands',
        roleType,
        toolPledged,
        true
      );

      showToast(`Mission enlisted! Exact address revealed: ${mission.exactAddress}`);
      onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to enlist in mission.';
      showToast(msg, 'warning');
    }
  });

  const modal = document.getElementById('modal-enlist');
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) {
      onCancel();
    }
  });
}
